import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import moment from "moment-timezone";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Inbox, Search } from "lucide-react";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../../../../../../../components/ui/input-group";
import Spinner from "../../../../../../../components/shared/Spinner";
import Button from "../../../../../../../components/shared/Button";
import Item from "./Item";
import useApi from "../../../../../../../utils/api";
import { applyTimezone } from "../../../../../../../utils/helpers/date";
import type { FetchShiftTradeItemsResult, SectionsDisplayProps } from "../types";

function SectionsDisplay({
    sections,
    selectedUserShifts,
    setSelectedUserShifts,
    error,
    setError,
    height,
    type,
    shiftId,
    view
}: SectionsDisplayProps) {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState("");
    const [search, setSearch] = useState("");
    const { authGet } = useApi();

    const fetchUsers = async ({ pageParam = 0 }): Promise<FetchShiftTradeItemsResult> => {
        const section = sections?.[0];
        if (!section) return { items: [], total: 0 };

        const location = section?.location?.locationId;
        const job = section?.job?.jobId;
        const timezone = section?.location?.timezoneValue;

        const startTime = applyTimezone(moment(section?.start), timezone, true);
        const endTime = applyTimezone(moment(sections?.[sections.length - 1]?.end), timezone, true);

        const params = {
            search,
            job,
            location,
            shiftId,
            startTime: moment(startTime).toISOString(true),
            endTime: moment(endTime).toISOString(true),
            groupId: section?.group,
            page: pageParam,
            perPage: 20
        };

        const response = await authGet(`/schedules/v2/${type}/users`, { params });

        const values = response?.data?.events || response?.data?.users || [];
        const total = response?.meta?.total || 0;

        return {
            items: values,
            total
        };
    };

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        // refetch,
    } = useInfiniteQuery({
        queryKey: [
            "available-users",
            type,
            shiftId,
            search,
            sections?.[0]?.start,
            sections?.[sections.length - 1]?.end,
        ],
        queryFn: fetchUsers,
        getNextPageParam: (lastPage, allPages) => {
            if (!lastPage) return undefined;

            const loaded = allPages.flatMap((p) => p.items).length;
            return loaded < lastPage.total ? allPages.length : undefined;
        },
        initialPageParam: 0,
        enabled: !!sections?.length,
        refetchOnWindowFocus: false,
    });

    const items = useMemo(
        () => data?.pages.flatMap((p) => p.items) || [],
        [data]
    );

    const onSearchChange = useMemo(
        () =>
            debounce((e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                setSearch(value);
                setSelectedUserShifts([]);
                // refetch();
            }, 600),
        [setSelectedUserShifts]
    );

    useEffect(() => {
        return () => onSearchChange.cancel();
    }, [onSearchChange]);

    const onSelectAll = useCallback(() => {
        setSelectedUserShifts(items);
        setError(null);
    }, [items, setSelectedUserShifts, setError]);

    const onSelectNone = useCallback(() => {
        setSelectedUserShifts([]);
    }, [setSelectedUserShifts]);

    const applySearchFilter = useMemo(
        () =>
            debounce((value: string) => {
                setSearch(value);
                setSelectedUserShifts([]);
            }, 1000),
        [setSelectedUserShifts]
    );

    useEffect(() => {
        return () => applySearchFilter.cancel();
    }, [applySearchFilter]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        applySearchFilter(value);
    };

    return (
        <>
            <InputGroup className="tw-mb-3">
                <InputGroupAddon>
                    <Search />
                </InputGroupAddon>
                <InputGroupInput
                    value={inputValue}
                    placeholder={t("search") + "..."}
                    onChange={handleInputChange}
                    disabled={isFetching || view}
                />
            </InputGroup>

            {items.length === 0 && isFetching ? (
                <div className="tw-flex tw-w-full tw-items-center tw-justify-center tw-py-5">
                    <Spinner />
                </div>
            ) : data?.pages?.[0]?.total > 0 ? (
                <div style={{ height: `calc(100% - ${height || 0}px - 120px)` }}>
                    <div className="tw-h-full">
                        <div className="tw-w-full tw-flex tw-items-center tw-justify-end tw-gap-2 tw-my-3">
                            <span
                                className="tw-text-primary tw-font-bold tw-cursor-pointer"
                                onClick={view ? undefined : onSelectAll}
                            >
                                {t("select-all")}
                            </span>
                            {" | "}
                            <span
                                className="tw-text-primary font-weight-bold tw-cursor-pointer"
                                onClick={view ? undefined : onSelectNone}
                            >
                                {t("select-none")}
                            </span>
                        </div>

                        {error && (
                            <div className="tw-mb-1 tw-text-rose-500 d-block">
                                {t(error)}
                            </div>
                        )}

                        <div
                            id="scrollable"
                            style={{ height: "calc(100% - 50px)", overflow: "auto" }}
                        >
                            <div
                                className="tw-mb-3"
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gridGap: 10,
                                }}
                            >
                                {items.map((item) => (
                                    <Item
                                        key={item.id}
                                        item={item}
                                        type={type}
                                        selectedUserShifts={selectedUserShifts}
                                        setSelectedUserShifts={setSelectedUserShifts}
                                        setError={setError}
                                        view={view}
                                    />
                                ))}
                            </div>

                            {hasNextPage && (
                                <div className="tw-flex tw-w-full tw-justify-center tw-my-2">
                                    <Button
                                        type="button"
                                        onClick={() => fetchNextPage()}
                                        loading={isFetchingNextPage}
                                        disabled={isFetchingNextPage}
                                    >
                                        {t("load-more")}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="tw-flex tw-flex-col tw-text-neutral-400 tw-gap-2 tw-items-center tw-py-5 tw-border tw-rounded-sm">
                    <Inbox />
                    <h4 className="text-gray font-weight-400">
                        {t("no-shift-to-trade", { trade: type })}
                    </h4>
                </div>
            )}
        </>
    );
}

export default SectionsDisplay;
